import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IGroupEntityDTO, ITemplateEntityDTO, TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import Debug from 'debug';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import * as fromTemplatesActions from '../actions/templates.actions';
import { AumicoTemplatesService } from '../services/templates.service';

const debug = Debug('aumico:aumico-lib-templates-fe:TemplatesEffects');

@Injectable()
export class TemplatesEffects {
    constructor(private actions$: Actions, private service: AumicoTemplatesService) {}

    //Groups
    getGroups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.getGroups.type),
            mergeMap(() => {
                return this.service.getGroups().pipe(
                    map((response: IGroupEntityDTO[]) => {
                        debug(response);
                        return fromTemplatesActions.onGetGroupsSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromTemplatesActions.onGetGroupsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    createGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.createGroup.type),
            mergeMap((payload) => {
                return this.service.createGroup(payload).pipe(
                    map((response: IGroupEntityDTO) => {
                        debug(response);
                        return fromTemplatesActions.onCreateGroupSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromTemplatesActions.onCreateGroupFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.updateGroup.type),
            mergeMap((payload) => {
                return this.service.updateGroup(payload).pipe(
                    map((response: IGroupEntityDTO) => {
                        return fromTemplatesActions.onUpdateGroupSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromTemplatesActions.onUpdateGroupFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    deleteGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.deleteGroup.type),
            mergeMap((action: fromTemplatesActions.ActionWithPayload<string>) => {
                return this.service.deleteGroup(action.payload).pipe(
                    map(() => {
                        return fromTemplatesActions.onDeleteGroupSuccessfully({ payload: action.payload });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromTemplatesActions.onDeleteGroupFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    errorOnGroups$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    fromTemplatesActions.onGetGroupsFailed.type,
                    fromTemplatesActions.onCreateGroupFailed.type,
                    fromTemplatesActions.onUpdateGroupFailed.type,
                    fromTemplatesActions.onDeleteGroupFailed.type,
                ),
                tap((action: fromTemplatesActions.ActionWithPayload<any>) => this.handleAllErrors(action.payload)),
            ),
        { dispatch: false },
    );

    getTranslations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.getTranslations),
            mergeMap((action) => {
                return this.service.getTranslations(action.locales, action.untranslatedOnly).pipe(
                    map((response) => {
                        return fromTemplatesActions.onGetTranslationsSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error) => {
                        return of(fromTemplatesActions.onGetTranslationsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateTranslations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.updateTranslations),
            mergeMap((action) => {
                const observable$ = this.service.updateTranslations(action.payload);
                return observable$.pipe(
                    map(() => {
                        return fromTemplatesActions.onUpdateTranslationsSuccess();
                    }),
                    catchError((error) => {
                        return of(
                            fromTemplatesActions.onUpdateTranslationsFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    //Templates
    getTemplates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.getTemplates.type),
            mergeMap(() => {
                return this.service.getTemplates().pipe(
                    map((response: ITemplateEntityDTO[]) => {
                        debug(response);
                        return fromTemplatesActions.onGetTemplatesSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromTemplatesActions.onGetTemplatesFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getTemplateByLegalFormAndAccountBasis$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.getTemplateByLegalFormAndAccountBasis.type),
            mergeMap((payload) => {
                return this.service.getTemplateByLegalFormAndAccountBasis(payload).pipe(
                    map((response: ITemplateEntityDTO) => {
                        debug(response);
                        return fromTemplatesActions.onGetTemplateByLegalFormAndAccountBasisSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromTemplatesActions.onGetTemplateByLegalFormAndAccountBasisFailed({ payload: error }),
                        );
                    }),
                );
            }),
        ),
    );

    getTemplateById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.getTemplateById.do.type),
            mergeMap((action: fromTemplatesActions.ActionWithPayload<string>) => {
                return this.service.getTemplateById(action.payload).pipe(
                    map((response: ITemplateEntityDTO) => {
                        debug(response);
                        return fromTemplatesActions.getTemplateById.success({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromTemplatesActions.getTemplateById.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    createTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.createTemplate.type),
            mergeMap((payload) => {
                return this.service.createTemplate(payload).pipe(
                    map((response: ITemplateEntityDTO) => {
                        debug(response);
                        return fromTemplatesActions.onCreateTemplateSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromTemplatesActions.onCreateTemplateFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    updateTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.updateTemplate.type),
            mergeMap((payload) => {
                return this.service.updateTemplate(payload).pipe(
                    map((response: ITemplateEntityDTO) => {
                        return fromTemplatesActions.onUpdateTemplateSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromTemplatesActions.onUpdateTemplateFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getAllTemplatesNamesAndIdsByTemplateType$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                fromTemplatesActions.getAllTemplatesNamesAndIdsByTemplateType.getAllTemplatesNamesAndIdsByTemplateType
                    .type,
            ),
            mergeMap((action: fromTemplatesActions.ActionWithPayload<TemplateType>) => {
                return this.service.getAllTemplatesNamesAndIdsByTemplateType$(action.payload).pipe(
                    map((response) => {
                        return fromTemplatesActions.getAllTemplatesNamesAndIdsByTemplateType.getAllTemplatesNamesAndIdsByTemplateTypeSuccess(
                            {
                                payload: response,
                            },
                        );
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromTemplatesActions.getAllTemplatesNamesAndIdsByTemplateType.getAllTemplatesNamesAndIdsByTemplateTypeFailed(
                                {
                                    payload: error,
                                },
                            ),
                        );
                    }),
                );
            }),
        ),
    );

    getTemplatesTargetTranslations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTemplatesActions.getTemplatesTargetTranslations.do),
            mergeMap((action) => {
                return this.service.getTemplatesTargetTranslations(action.payload).pipe(
                    map((response) => {
                        return fromTemplatesActions.getTemplatesTargetTranslations.success({
                            payload: response,
                        });
                    }),
                    catchError((error) => {
                        return of(fromTemplatesActions.getTemplatesTargetTranslations.failure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    errorOnTemplates$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    fromTemplatesActions.onGetTemplatesFailed.type,
                    fromTemplatesActions.onCreateTemplateFailed.type,
                    fromTemplatesActions.onUpdateTemplateFailed.type,
                    fromTemplatesActions.onGetTemplateByLegalFormAndAccountBasisFailed.type,
                ),
                tap((action: fromTemplatesActions.ActionWithPayload<any>) => this.handleAllErrors(action.payload)),
            ),
        { dispatch: false },
    );

    public handleAllErrors(error) {
        debug(error);
        return error;
    }
}
